// ─────────────────────────────────────────────────────────────
//  «Мои разборы»: завершённые расклады и астро-разборы хранятся в БД.
//
//  АСТРОЛОГИЯ (вкладка 1) лежит в БД (user_astro_readings): каждая генерация
//  сохраняется целиком — формула души, все пункты, итог, данные рождения. Такой
//  разбор можно перечитать через полгода, скачать PDF-файлом в личку и вернуть в
//  чат с ИИ (бэк заново собирает якорь из БД и кладёт его в Redis).
//
//  ТАРО сохраняет карты, вопросы и толкования после генерации. Старый список тем
//  остаётся ниже: он полезен для тем, которые выбирали без готового расклада.
// ─────────────────────────────────────────────────────────────
import { useEffect, useState } from 'react'
import {
  ArrowLeft, CheckCircle2, ChevronRight, FileDown, MessageCircle, Sparkles, Trash2,
} from 'lucide-react'
import {
  chatAboutAstroHistoryEntry, deleteAstroHistoryEntry, fetchAstroHistory, fetchAstroHistoryEntry,
  chatAboutSavedTarotReading, deleteSavedTarotReading, fetchSavedTarotReading, fetchSavedTarotReadings,
  fetchTarotHistory, getAiSession, isAccessError, selectTarotSpread, sendAstroHistoryPdf,
} from '../api.js'
import { cardFace } from '../cardFace.js'
import { closeApp, hapticNotification, showConfirm } from '../tg.js'
import Markdown from './Markdown.jsx'
import { Glass, Orb, Pill, SparkLine } from './ui.jsx'

const plural = (n, one, few, many) => {
  const m10 = n % 10, m100 = n % 100
  if (m10 === 1 && m100 !== 11) return one
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few
  return many
}
const nReadings = n => `${n} ${plural(n, 'разбор', 'разбора', 'разборов')}`
const nPoints = n => `${n} ${plural(n, 'пункт', 'пункта', 'пунктов')}`
const nTimes = n => `${n} ${plural(n, 'раз', 'раза', 'раз')}`

// Даты бэк отдаёт без зоны, но хранит в UTC — час пояса дописываем сами, иначе
// вечерний разбор у пользователя восточнее UTC показывался бы вчерашним днём.
function formatDate(iso) {
  if (!iso) return ''
  const date = new Date(/(Z|[+-]\d\d:?\d\d)$/.test(iso) ? iso : `${iso}Z`)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
}
// birth_date приходит как «YYYY-MM-DD» — это календарный день, а не момент времени,
// и поясов у него нет: собираем строку сами, чтобы дата не «уехала».
function formatBirthDate(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '')
  if (!m) return value || ''
  const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля',
    'августа', 'сентября', 'октября', 'ноября', 'декабря']
  return `${Number(m[3])} ${months[Number(m[2]) - 1]} ${m[1]}`
}

/**
 * Кнопка «прислать PDF в Telegram». Живёт и в архиве, и на экране только что
 * сделанного разбора (main.jsx, Formula), поэтому вынесена сюда целиком со своим
 * состоянием: снаружи нужен только id строки истории.
 *
 * Отказ Telegram приезжает как ok:false с человеческим текстом (обычно «нажми боту
 * /start») — это не ошибка сети, и показывать её надо как подсказку, а не как сбой.
 */
export function SendPdfButton({ entryId, label = 'Скачать разбор (PDF в Telegram)' }) {
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => { setSent(false); setError('') }, [entryId])

  const send = async () => {
    if (loading || !entryId) return
    setLoading(true); setError('')
    try {
      const result = await sendAstroHistoryPdf(entryId)
      if (result?.ok) { setSent(true); hapticNotification('success') }
      else { setError(result?.reason || 'Не удалось отправить файл, попробуй ещё раз.'); hapticNotification('error') }
    } catch (e) {
      setError(e?.message || 'Не удалось отправить файл. Проверь соединение и попробуй ещё раз.')
      hapticNotification('error')
    } finally { setLoading(false) }
  }

  return (
    <>
      <Pill className="archive-download" disabled={loading || !entryId} onClick={send}>
        {loading
          ? <><span className="button-spinner" /> Готовим файл…</>
          : sent
            ? <><CheckCircle2 size={16} /> Отправить ещё раз</>
            : <><FileDown size={16} /> {label}</>}
      </Pill>
      {sent && (
        <div className="archive-sent">
          <p>Готово! Разбор ушёл файлом в чат с ботом — его можно открыть прямо там,
            сохранить в телефон или переслать.</p>
          <button type="button" className="press" onClick={closeApp}>Открыть чат с ботом</button>
        </div>
      )}
      {error && <p className="api-error" role="alert">{error}</p>}
    </>
  )
}

/** Разбор целиком — та же вёрстка, что и на экране свежего разбора. */
function ReadingBody({ entry }) {
  const birth = [
    entry.name,
    formatBirthDate(entry.birth_date),
    (entry.birth_time || '').slice(0, 5),
    entry.birth_city,
  ].filter(Boolean).join(', ')
  const partner = entry.partner_name && [
    entry.partner_name,
    formatBirthDate(entry.partner_birth_date),
    (entry.partner_birth_time || '').slice(0, 5),
    entry.partner_birth_city,
  ].filter(Boolean).join(', ')

  return (
    <>
      <p className="archive-birth">
        <b>{birth}</b>
        {partner && <span>Второй человек: {partner}</span>}
        {!entry.city_recognized && <span>Город не распознан — время принято за московское</span>}
      </p>
      {entry.greeting && (
        <Glass className="formula-greeting"><Markdown text={entry.greeting} /></Glass>
      )}
      {entry.chart?.length > 0 && (
        <div className="formula-tags">{entry.chart.map((item, i) => <span key={`${item}-${i}`}>{item}</span>)}</div>
      )}
      {entry.partner_chart?.length > 0 && (
        <div className="formula-tags">{entry.partner_chart.map((item, i) => <span key={`p-${item}-${i}`}>{item}</span>)}</div>
      )}
      <div className="formula-points">
        {(entry.points || []).map((point, i) => (
          <Glass className="formula-point" key={`${point.point}-${i}`}>
            <span className="formula-point-num">Пункт {i + 1}</span>
            <b>{point.point}</b>
            <div className="formula-point-copy"><Markdown text={point.interpretation} /></div>
          </Glass>
        ))}
      </div>
      {entry.summary && (
        <Glass className="formula-total">
          <h3>Итог разбора</h3>
          <div className="formula-point-copy"><Markdown text={entry.summary} /></div>
        </Glass>
      )}
    </>
  )
}

/** Открытый разбор: заголовок, текст, возврат в чат, выгрузка и удаление. */
function AstroEntry({ entryId, onBack, onDeleted, onDiscuss, onAccessError }) {
  const [entry, setEntry] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let live = true
    setEntry(null); setError('')
    fetchAstroHistoryEntry(entryId)
      .then(data => { if (live) setEntry(data) })
      .catch(e => { if (live) setError(e?.message || 'Не удалось открыть разбор') })
    return () => { live = false }
  }, [entryId])

  // Вернуть разбор в чат: бэк пересобирает якорь из БД и кладёт его в Redis, после
  // чего ИИ отвечает по этому разбору, как сразу после генерации.
  const discuss = async () => {
    if (busy) return
    setBusy(true); setError('')
    try {
      await chatAboutAstroHistoryEntry(entryId)
      hapticNotification('success')
      onDiscuss()
    } catch (e) {
      if (isAccessError(e)) onAccessError?.(e)
      else setError(e?.detail || e?.message || 'Не удалось открыть разбор в чате')
    } finally { setBusy(false) }
  }

  // Удаление подтверждаем родным диалогом Telegram, а не своей модалкой: он
  // привычнее и его невозможно не заметить.
  const remove = async () => {
    if (busy) return
    if (!await showConfirm('Удалить этот разбор? Он исчезнет из «Моих разборов» навсегда.')) return
    setBusy(true)
    try {
      await deleteAstroHistoryEntry(entryId)
      hapticNotification('success')
      onDeleted()
    } catch (e) {
      setError(e?.message || 'Не удалось удалить разбор')
      hapticNotification('error')
    } finally { setBusy(false) }
  }

  return (
    <section className="screen formula-screen archive-entry">
      <header className="topbar">
        <button className="press" onClick={onBack}><ArrowLeft size={17} /> Назад</button>
        <span><Orb /> Мои разборы</span>
      </header>
      {error && <p className="api-error" role="alert">{error}</p>}
      {!entry && !error && <p className="result-placeholder">✨ Открываем разбор…</p>}
      {entry && <>
        <h2 className="page-title">{entry.title}</h2>
        <p className="subtitle">{entry.section} · сделан {formatDate(entry.created_at)}</p>
        <SparkLine />
        <ReadingBody entry={entry} />
        <Pill className="archive-discuss" disabled={busy} onClick={discuss}>
          <MessageCircle size={16} /> Обсудить разбор с ИИ
        </Pill>
        <p className="archive-hint">Разбор подставится в чат Clavis целиком — можно спрашивать
          по нему что угодно. Прошлая переписка в чате при этом очистится.</p>
        <SendPdfButton entryId={entry.id} />
        <button type="button" className="archive-delete press" disabled={busy} onClick={remove}>
          <Trash2 size={15} /> Удалить разбор
        </button>
      </>}
    </section>
  )
}

/** Вкладка «Астрология»: разборы из БД — открыть, обсудить, скачать. */
function AstroTab({ items, onOpen, onNew }) {
  if (items === null) return <p className="result-placeholder">✨ Загружаем разборы…</p>
  if (!items.length) {
    return (
      <Glass className="archive-empty">
        <Orb />
        <b>Разборов пока нет</b>
        <span>Сделай первый разбор Формулы души — он сохранится здесь навсегда, и к нему
          можно будет вернуться, обсудить с ИИ или скачать файлом.</span>
        <Pill onClick={onNew}><Sparkles size={16} /> Сделать разбор</Pill>
      </Glass>
    )
  }
  return (
    <div className="reading-list">
      {items.map(item => (
        <button type="button" className="reading-row press" key={item.id} onClick={() => onOpen(item.id)}>
          <Orb />
          <span>
            <b>{item.title}</b>
            <small>
              {item.section} · {formatDate(item.created_at)} · {nPoints(item.points_count)}
              {item.has_partner ? ' · вдвоём' : ''}
              {item.export_count ? ' · скачан' : ''}
            </small>
          </span>
          <ChevronRight />
        </button>
      ))}
    </div>
  )
}

function SavedTarotEntry({ entryId, onBack, onDeleted, onDiscuss, onAccessError }) {
  const [entry, setEntry] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    let live = true
    fetchSavedTarotReading(entryId).then(value => { if (live) setEntry(value) })
      .catch(e => { if (live) setError(e?.message || 'Не удалось открыть расклад') })
    return () => { live = false }
  }, [entryId])

  const discuss = async () => {
    setBusy(true); setError('')
    try {
      await chatAboutSavedTarotReading(entryId)
      hapticNotification('success')
      onDiscuss()
    } catch (e) {
      if (isAccessError(e)) onAccessError?.(e)
      else setError(e?.detail || e?.message || 'Не удалось открыть расклад в чате')
    } finally { setBusy(false) }
  }
  const remove = async () => {
    if (!await showConfirm('Удалить этот расклад из профиля?')) return
    setBusy(true); setError('')
    try { await deleteSavedTarotReading(entryId); hapticNotification('success'); onDeleted() }
    catch (e) { setError(e?.message || 'Не удалось удалить расклад') }
    finally { setBusy(false) }
  }
  return <section className="screen archive-entry reading-screen">
    <header className="topbar"><button className="press" onClick={onBack}><ArrowLeft size={17}/> Назад</button><span><Orb/> Мои расклады</span></header>
    {error && <p className="api-error" role="alert">{error}</p>}
    {!entry && !error && <p className="result-placeholder">✨ Открываем расклад…</p>}
    {entry && <>
      <h2 className="page-title">{entry.title}</h2>
      <p className="subtitle">{entry.sphere} · {formatDate(entry.created_at)}</p>
      <SparkLine/>
      <Glass className="result"><p><b>{entry.name}</b> · {formatBirthDate(entry.birth_date)}</p><p>{entry.situation}</p>{entry.greeting && <div className="result-greeting"><Markdown text={entry.greeting}/></div>}</Glass>
      {(entry.answers || []).map((answer, index) => <div key={index}>
        <div className={`cards-pick${(answer.cards || []).length < 3 ? ' is-short' : ''}`}>
          {(answer.cards || []).map((name, cardIndex) => <figure key={cardIndex} className={`card-open${/\(перев/i.test(name) ? ' is-reversed' : ''}`}><img src={cardFace(name)} alt={name}/></figure>)}
        </div>
        <Glass className="result"><h3>{answer.question || entry.questions?.[index] || `Вопрос ${index + 1}`}</h3><p className="result-cards">{(answer.cards || []).join(' · ')}</p><Markdown text={answer.interpretation}/></Glass>
      </div>)}
      {entry.summary && <Glass className="result"><h3>Итог расклада</h3><Markdown text={entry.summary}/></Glass>}
      {entry.advice && <Glass className="result"><h3>Совет карт</h3><Markdown text={entry.advice}/></Glass>}
      <Pill className="archive-discuss" disabled={busy} onClick={discuss}><MessageCircle size={16}/> Обсудить расклад с ИИ</Pill>
      <button type="button" className="archive-delete press" disabled={busy} onClick={remove}><Trash2 size={15}/> Удалить расклад</button>
    </>}
  </section>
}

function SavedTarotTab({ items, onOpen, onNew, onMore, hasMore, loadingMore }) {
  if (items === null) return <p className="result-placeholder">✨ Загружаем расклады…</p>
  if (!items.length) return <Glass className="archive-empty"><Orb/><b>Сохранённых раскладов пока нет</b><span>Новый расклад сохранится здесь целиком: карты, вопросы и толкования.</span><Pill onClick={onNew}><Sparkles size={16}/> Сделать расклад</Pill></Glass>
  return <><div className="reading-list">{items.map(item => <button type="button" className="reading-row press" key={item.id} onClick={() => onOpen(item.id)}><Orb/><span><b>{item.title}</b><small>{item.sphere} · {formatDate(item.created_at)}</small></span><ChevronRight/></button>)}</div>{hasMore && <Pill onClick={onMore} disabled={loadingMore}>{loadingMore ? 'Загружаем…' : 'Показать ещё расклады'}</Pill>}</>
}

/** Вкладка «Таро»: темы прошлых раскладов — вернуться к разговору. */
function TarotTab({ items, onOpenChat, onAccessError, onNew }) {
  const [busyId, setBusyId] = useState(null)
  const [error, setError] = useState('')

  const resume = async spreadId => {
    if (busyId) return
    setBusyId(spreadId); setError('')
    try {
      await selectTarotSpread(spreadId)
      hapticNotification('success')
      onOpenChat()
    } catch (e) {
      if (isAccessError(e)) onAccessError?.(e)
      else setError(e?.message || 'Не удалось вернуться к теме')
    } finally { setBusyId(null) }
  }

  if (items === null) return <p className="result-placeholder">✨ Загружаем расклады…</p>
  if (!items.length) {
    return (
      <Glass className="archive-empty">
        <Orb />
        <b>Раскладов пока не было</b>
        <span>Сделай расклад — и сюда попадёт его тема, чтобы можно было вернуться
          к разговору с ИИ.</span>
        <Pill onClick={onNew}><Sparkles size={16} /> Выбрать расклад</Pill>
      </Glass>
    )
  }
  return (
    <>
      <p className="archive-note">
        Здесь темы, которые выбирали для чата. Завершённые расклады с картами и
        толкованиями сохранены выше.
      </p>
      {error && <p className="api-error" role="alert">{error}</p>}
      <div className="reading-list">
        {items.map(item => (
          <div className="history-card" key={item.spread_id}>
            <Orb />
            <span>
              <b>{item.title}</b>
              <small>
                {item.sphere} · {item.last_reading_at
                  ? `расклад ${formatDate(item.last_reading_at)}`
                  : `открывали ${formatDate(item.last_at)}`}
                {item.times > 1 ? ` · ${nTimes(item.times)}` : ''}
              </small>
            </span>
            {item.is_active
              ? <button type="button" className="press history-resume" disabled={busyId === item.spread_id}
                onClick={() => resume(item.spread_id)}>
                {busyId === item.spread_id ? <span className="button-spinner" /> : <MessageCircle size={15} />}
                Вернуться
              </button>
              : <em>убран из каталога</em>}
          </div>
        ))}
      </div>
    </>
  )
}

/** Что сейчас подставлено в чат — из Redis (GET /ai/session). */
function ActiveTopic({ session, onOpenChat }) {
  if (!session) return null
  const kind = session.module === 'tarot' ? 'Расклад Таро'
    : session.module === 'astrology' ? 'Астро-разбор' : 'Свободный чат'
  return (
    <Glass className="history-active">
      <span className="history-active-kicker">Сейчас в чате · {kind}</span>
      <b>{session.title}</b>
      {session.subtitle && <small>{session.subtitle}</small>}
      <p>{session.has_reading
        ? 'Разбор подставлен в чат целиком — ИИ помнит его и ответит по нему.'
        : 'Тема выбрана, разбора в ней ещё не было — ИИ отвечает по теме.'}</p>
      <Pill onClick={onOpenChat}><MessageCircle size={15} /> Продолжить разговор</Pill>
    </Glass>
  )
}

export default function HistoryScreen({ go, back, astroScreen, tarotScreen, chatScreen, onAccessError }) {
  const [tab, setTab] = useState('tarot')
  const [astro, setAstro] = useState(null)
  const [tarot, setTarot] = useState(null)
  const [topics, setTopics] = useState(null)
  const [tarotMore, setTarotMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [session, setSession] = useState(null)
  const [error, setError] = useState('')
  const [openId, setOpenId] = useState(null)
  const [openTarotId, setOpenTarotId] = useState(null)

  // Обе вкладки и активная тема грузятся сразу: списки короткие, зато счётчики на
  // вкладках не скачут при переключении, а «сейчас в чате» видно с первого экрана.
  const load = async () => {
    setError('')
    const [astroRows, tarotRows, topicRows, current] = await Promise.all([
      fetchAstroHistory().catch(e => { setError(e?.message || 'Не удалось загрузить разборы'); return [] }),
      fetchSavedTarotReadings().catch(e => { setError(e?.message || 'Не удалось загрузить расклады'); return [] }),
      fetchTarotHistory().catch(() => []),
      getAiSession().catch(() => null),
    ])
    setAstro(astroRows); setTarot(tarotRows); setTarotMore(tarotRows.length === 50); setTopics(topicRows); setSession(current)
  }

  const loadMoreTarot = async () => {
    if (loadingMore || !tarot?.length) return
    setLoadingMore(true)
    try {
      const next = await fetchSavedTarotReadings(50, tarot.length)
      setTarot((current) => [...(current || []), ...next])
      setTarotMore(next.length === 50)
    } catch (e) { setError(e?.message || 'Не удалось загрузить следующие расклады') }
    finally { setLoadingMore(false) }
  }

  useEffect(() => { load() }, [])

  if (openId) {
    return <AstroEntry
      entryId={openId}
      onBack={() => { setOpenId(null); load() }}
      onDeleted={() => { setOpenId(null); setAstro(null); load() }}
      onDiscuss={() => go(chatScreen)}
      onAccessError={onAccessError}
    />
  }
  if (openTarotId) return <SavedTarotEntry entryId={openTarotId}
    onBack={() => setOpenTarotId(null)}
    onDeleted={() => { setOpenTarotId(null); setTarot(null); load() }}
    onDiscuss={() => go(chatScreen)} onAccessError={onAccessError}/>

  const tabs = [
    { id: 'astro', label: 'Астрология', count: astro?.length },
    { id: 'tarot', label: 'Таро', count: tarot?.length },
  ]

  return (
    <section className="screen archive-screen">
      <header className="topbar">
        <button className="press" onClick={() => go(back)}><ArrowLeft size={17} /> Назад</button>
        <span><Orb /> Мои разборы</span>
      </header>
      <h2 className="page-title">Мои разборы</h2>
      <p className="subtitle">Расклады Таро и астро-разборы можно открыть и перечитать здесь</p>
      <SparkLine />

      {error && <p className="api-error" role="alert">{error}</p>}

      <ActiveTopic session={session} onOpenChat={() => go(chatScreen)} />

      <div className="history-tabs">
        {tabs.map(item => (
          <button type="button" key={item.id} className={`press ${tab === item.id ? 'is-active' : ''}`}
            onClick={() => setTab(item.id)}>
            {item.label}{item.count ? <em>{item.count}</em> : ''}
          </button>
        ))}
      </div>

      {tab === 'astro'
        ? <AstroTab items={astro} onOpen={setOpenId} onNew={() => go(astroScreen)} />
        : <><SavedTarotTab items={tarot} onOpen={setOpenTarotId} onNew={() => go(tarotScreen)} onMore={loadMoreTarot} hasMore={tarotMore} loadingMore={loadingMore}/>
          {topics?.length > 0 && <><h3 className="archive-subtitle">Темы разговоров</h3><TarotTab items={topics} onOpenChat={() => go(chatScreen)} onAccessError={onAccessError} onNew={() => go(tarotScreen)}/></>}
        </>}

      {tab === 'astro' && astro?.length > 0 && (
        <p className="archive-note">
          Разборы Формулы души сохраняются целиком: их можно перечитать, обсудить с ИИ
          и скачать PDF-файлом — он придёт в чат с ботом.
        </p>
      )}
    </section>
  )
}
